import { UploadOutlined } from "@ant-design/icons";
import { AccelFile, FileSize, FileType } from "@axl/membership-sdk";
import { PropsWithChildren, useCallback, useEffect, useMemo } from "react";
import { useDropzone } from "react-dropzone";

import { useLoc } from "@app/shared/lib/hooks";
import { toMimeTypesString } from "@app/shared/lib/to-mime-types";
import withHandler from "@app/shared/lib/with-handler";

import styles from "./FileDropzone.module.scss";

export type FileDropzoneHandler = {
  open: () => void;
};

type FileDropzoneProps = {
  accept?: string;
  acceptTypes?: FileType[];
  onUpload?: (files: AccelFile[]) => void;
  onCancel?: () => void;
  minSize?: FileSize;
  maxSize?: FileSize;
  disabled?: boolean;
  multiple?: boolean;
  noClick?: boolean;
  defaultOpen?: boolean;
  noDrag?: boolean;
  className?: string;
  placeholder?: React.ReactNode;
  handle?: FileDropzoneHandler;
};

const FileDropzone: React.FC<PropsWithChildren<FileDropzoneProps>> = ({
  accept,
  acceptTypes,
  children,
  onUpload,
  onCancel,
  disabled,
  multiple = true,
  minSize,
  maxSize,
  noClick,
  noDrag,
  defaultOpen,
  className,
  handle,
  placeholder,
}) => {
  const loc = useLoc();

  const acceptMimeTypes = useMemo(() => {
    if (accept) return accept;
    if (!acceptTypes) return undefined;

    return toMimeTypesString(acceptTypes, true);
  }, [accept, acceptTypes]);

  const onDrop = useCallback(
    (files: File[]) => {
      if (files.length === 0) return;
      const accelFiles = files.map((x) => AccelFile.fromFile(x));
      onUpload?.(accelFiles);
    },
    [onUpload],
  );

  const onFileDialogCancel = useCallback(() => {
    onCancel?.();
  }, [onCancel]);

  const { open, getRootProps, getInputProps } = useDropzone({
    onDrop,
    onFileDialogCancel,
    multiple,
    accept: acceptMimeTypes,
    disabled,
    minSize: minSize?.bytes,
    maxSize: maxSize?.bytes,
    noClick,
    noDrag,
  });

  useEffect(() => {
    // eslint-disable-next-line no-param-reassign
    if (handle) handle.open = () => open();

    if (defaultOpen) open();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div {...getRootProps()} className={className}>
      <input {...getInputProps()} />
      {children ?? (
        <div
          className={styles.dropzone_holder}
          data-disabled={disabled}
          data-testid="file-dropzone"
        >
          <UploadOutlined className="mr-20" />
          <span>
            {placeholder ??
              loc.word("FileUploader.uploadMsg", {
                default: "Click or drag files to upload",
              })}
          </span>
        </div>
      )}
    </div>
  );
};

// TODO replace with withHandler2
export default withHandler<FileDropzoneProps, FileDropzoneHandler>(
  FileDropzone,
);
